/**
 * Bucketing for the photo-quality analytics events.
 *
 * Raw floats must never reach PostHog. `sharpness` is unbounded and would be
 * pure cardinality, which makes the warning-precision funnel unqueryable.
 * Every bucketer is total: non-finite, NaN, and undefined all return
 * 'unknown' rather than throwing or leaking a raw value.
 *
 * Boundaries are half-open and lower-inclusive: 20 lands in '20_40'.
 */

import type {
  PhotoQualityMetrics,
  PhotoQualityReason,
  PhotoQualityVerdict,
} from './photo-quality'

export type MetricBucket = string

export type PhotoRole = 'primary' | 'supporting'

/** 'none' is the passing population, which only `capture_quality_measured` emits. */
export type PhotoQualityReasonProp = PhotoQualityReason | 'none'

const UNKNOWN: MetricBucket = 'unknown'

function isMeasurable(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value)
}

/**
 * Pick the bucket for `value` from ascending half-open lower-inclusive edges.
 * Labels must be `edges.length + 1` long: below the first edge, each span, and
 * at or above the last edge.
 */
function bucketBy(value: number, edges: number[], labels: string[]): MetricBucket {
  for (let i = 0; i < edges.length; i++) {
    if (value < edges[i]) return labels[i]
  }
  return labels[edges.length]
}

/** 'lt20' | '20_40' | '40_60' | '60_100' | '100_300' | 'gte300' | 'unknown' */
export function bucketSharpness(value: number): MetricBucket {
  if (!isMeasurable(value)) return UNKNOWN
  return bucketBy(
    value,
    [20, 40, 60, 100, 300],
    ['lt20', '20_40', '40_60', '60_100', '100_300', 'gte300'],
  )
}

/** Width-20 bands across the 0-255 luma range. 'lt20' ... '200_220' | 'gte220'. */
export function bucketLuma(value: number): MetricBucket {
  if (!isMeasurable(value)) return UNKNOWN
  const edges = [20, 40, 60, 80, 100, 120, 140, 160, 180, 200, 220]
  const labels = [
    'lt20',
    '20_40',
    '40_60',
    '60_80',
    '80_100',
    '100_120',
    '120_140',
    '140_160',
    '160_180',
    '180_200',
    '200_220',
    'gte220',
  ]
  return bucketBy(value, edges, labels)
}

/**
 * Coarse contrast needs finer low bands than luma: blur judgeability flips at
 * MIN_COARSE_CONTRAST_TO_JUDGE_BLUR (2.5), so a single 'lt20' band would hide
 * the abstain boundary from tuning.
 * 'lt2.5' | '2.5_5' | '5_10' | '10_20' | '20_40' | '40_60' | 'gte60' | 'unknown'
 */
export function bucketCoarseContrast(value: number): MetricBucket {
  if (!isMeasurable(value)) return UNKNOWN
  return bucketBy(
    value,
    [2.5, 5, 10, 20, 40, 60],
    ['lt2.5', '2.5_5', '5_10', '10_20', '20_40', '40_60', 'gte60'],
  )
}

/**
 * Clip ratios in percent-of-frame bands. Edges align to the exposure
 * thresholds (BRIGHT_CLIP_RATIO_MAX 32%, DARK_CLIP_RATIO_MAX 55%) so the
 * events can tune them. Input is a 0..1 ratio; labels are percent.
 * 'lt1' | '1_5' | '5_10' | '10_20' | '20_32' | '32_55' | 'gte55' | 'unknown'
 */
export function bucketClipRatio(value: number): MetricBucket {
  if (!isMeasurable(value)) return UNKNOWN
  return bucketBy(
    value,
    [0.01, 0.05, 0.1, 0.2, 0.32, 0.55],
    ['lt1', '1_5', '5_10', '10_20', '20_32', '32_55', 'gte55'],
  )
}

/** 'lt50' | '50_150' | '150_400' | '400_1000' | 'gte1000' | 'unknown' */
export function bucketDurationMs(value: number): MetricBucket {
  if (!isMeasurable(value)) return UNKNOWN
  return bucketBy(
    value,
    [50, 150, 400, 1000],
    ['lt50', '50_150', '150_400', '400_1000', 'gte1000'],
  )
}

export interface PhotoQualityAnalyticsProps {
  quality_reason: PhotoQualityReasonProp
  photo_role: PhotoRole
  sharpness_bucket: MetricBucket
  mean_luma_bucket: MetricBucket
  luma_stddev_bucket: MetricBucket
  coarse_contrast_bucket: MetricBucket
  clipped_dark_ratio_bucket: MetricBucket
  clipped_bright_ratio_bucket: MetricBucket
  sampled_pixels: number
  measure_ms_bucket: MetricBucket
}

export interface PhotoQualityAnalyticsInput {
  reason: PhotoQualityReasonProp
  metrics: PhotoQualityMetrics
  role: PhotoRole
  measureMs: number
}

export function photoQualityAnalyticsProps(
  warning: PhotoQualityAnalyticsInput,
): PhotoQualityAnalyticsProps {
  const metrics = warning?.metrics
  const width = isMeasurable(metrics?.sampledWidth) ? metrics.sampledWidth : 0
  const height = isMeasurable(metrics?.sampledHeight) ? metrics.sampledHeight : 0

  return {
    quality_reason: warning.reason,
    photo_role: warning.role,
    sharpness_bucket: bucketSharpness(metrics?.sharpness as number),
    mean_luma_bucket: bucketLuma(metrics?.meanLuma as number),
    luma_stddev_bucket: bucketLuma(metrics?.lumaStdDev as number),
    coarse_contrast_bucket: bucketCoarseContrast(metrics?.coarseContrast as number),
    clipped_dark_ratio_bucket: bucketClipRatio(metrics?.clippedDarkRatio as number),
    clipped_bright_ratio_bucket: bucketClipRatio(metrics?.clippedBrightRatio as number),
    sampled_pixels: Math.round(width * height),
    measure_ms_bucket: bucketDurationMs(warning?.measureMs),
  }
}

export interface PhotoQualityMeasurementProps extends PhotoQualityAnalyticsProps {
  /** true when a warning was shown for this measurement. */
  warned: boolean
  /**
   * false when the frame carried too little coarse structure to judge blur.
   * Without this the abstain population is indistinguishable from a clean pass,
   * and a threshold can only ever be found too loose, never too tight.
   */
  blur_judgeable: boolean
}

/**
 * Props for the every-measurement event. Warnings alone describe only the
 * flagged tail; tuning a threshold in either direction needs the pass
 * distribution too, so this is emitted for ok verdicts as well.
 */
export function photoQualityMeasurementProps(input: {
  verdict: PhotoQualityVerdict
  role: PhotoRole
  measureMs: number
}): PhotoQualityMeasurementProps {
  const { verdict } = input
  return {
    ...photoQualityAnalyticsProps({
      reason: verdict?.reason ?? 'none',
      metrics: verdict?.metrics as PhotoQualityMetrics,
      role: input.role,
      measureMs: input.measureMs,
    }),
    warned: Boolean(verdict?.reason),
    blur_judgeable: Boolean(verdict?.blurJudgeable),
  }
}
